"use client";

import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { Download, HelpCircle } from "lucide-react";
import { useStatisticsContext } from "./statistics_context";
import { get } from "../../lib/api";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";
import { ChartTooltip } from "../../components/ui/chart";
import LoadingScreenDenda from "../Unique/Animation/loading_screen";
import { useIsMobile } from "../../hooks/use-mobile";

const SimpleGraph = ({ x, y, type, isMobile }) => {
  const data = x.map((value, index) => ({ x: value, y: y[index] }));

  // Detect dark mode (no new imports)
  const isDark = typeof window !== 'undefined' && document.documentElement.classList.contains('dark');

  const axisStyle = { fill: isDark ? '#ffffff' : undefined };
  const gridStroke = isDark ? '#ffffff' : '#ccc';
  const lineStroke = isDark ? '#A8A6D8' : '#8884d8';
  const barFill = isDark ? '#A8A6D8' : '#8884d8';

  return (
    <ResponsiveContainer width="100%" height={isMobile ? 150 : 200}>
      {type === "scatter" ? (
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
          <XAxis dataKey="x" tick={{ ...axisStyle, fontSize: isMobile ? 10 : 12 }} />
          <YAxis tick={{ ...axisStyle, fontSize: isMobile ? 10 : 12 }} />
          <ChartTooltip />
          <Line type="monotone" dataKey="y" stroke={lineStroke} dot={false} activeDot={{ r: 6, stroke: lineStroke, fill: lineStroke }} />
        </LineChart>
      ) : (
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
          <XAxis dataKey="x" tick={{ ...axisStyle, fontSize: isMobile ? 10 : 12 }} />
          <YAxis tick={{ ...axisStyle, fontSize: isMobile ? 10 : 12 }} />
          <ChartTooltip />
          <Bar dataKey="y" fill={barFill} />
        </BarChart>
      )}
    </ResponsiveContainer>
  );
};

const FourGraph = ({ game_id }) => {
  const [retention, setRetention] = useState({});
  const [loading, setLoading] = useState(false);
  const { generalStatistics, generalStatsLoading } = useStatisticsContext();
  const isMobile = useIsMobile();

  const getDefaultDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 11);
    return date.toISOString().split("T")[0];
  };

  const [retentionDate, setRetentionDate] = useState(getDefaultDate());

  useEffect(() => {
    if (!game_id) return;

    const fetchRetentionRateData = async () => {
      setLoading(true);
      const data = await get(`/statistics?method=getRetentionRate&date=${retentionDate}&gameid=${game_id}`);
      setRetention(data);
      setLoading(false);
    };

    fetchRetentionRateData();
  }, [game_id, retentionDate]);

  const handleDateChange = (event) => {
    setRetentionDate(event.target.value);
  };

  if (generalStatsLoading || loading) {
    return <LoadingScreenDenda text="Loading..." />;
  }

  const handleDownload = () => {
    if (!generalStatistics?.date || !Array.isArray(generalStatistics.date)) return;

    const keys = Object.keys(generalStatistics).filter((key) => Array.isArray(generalStatistics[key]));
    const rowCount = generalStatistics.date.length;
    const rows = [];

    for (let i = 0; i < rowCount; i++) {
      const row = {};
      keys.forEach((key) => {
        row[key] = generalStatistics[key]?.[i] ?? "";
      });
      rows.push(row);
    }

    // Add retention data to each row, assuming same length or handling shorter arrays
    const retentionKeys = ["Day 0", "Day 1", "Day 7", "Day 10"];
    if (retention?.day1_retention_percentage?.[0] !== undefined) {
      const retentionValues = [100, retention.day1_retention_percentage[0], retention.day7_retention_percentage[0], retention.day10_retention_percentage[0]];
      rows.forEach((row, index) => {
        retentionKeys.forEach((key, idx) => {
          row[`Retention ${key}`] = retentionValues[idx] ?? "";
        });
      });
    }

    const headers = Object.keys(rows[0]);
    const csvData = rows.map((row) => headers.map((h) => row[h]).join(",")).join("\n");
    const csv = `${headers.join(",")}\n${csvData}`;

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Users Stats for Game ID ${game_id}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card className={`bg-[#ffffff] text-[#232833] border-0 dark:bg-[#232833] dark:text-[#ffffff] overflow-hidden ${isMobile ? 'w-full' : ''}`} 
          style={{width: isMobile ? '100%' : '60vw'}}>
      <div className="space-y-4">
        <div className={`grid grid-cols-1 ${isMobile ? 'gap-2' : 'md:grid-cols-2 lg:grid-cols-2 gap-4'} grid-rows-2`}>
          <Card className="bg-[#ffffff] text-[#232833] border-0 shadow-none rounded-lg dark:bg-[#2c313c] dark:text-[#ffffff] overflow-hidden">
            <CardHeader className={isMobile ? 'pb-2' : ''}>
              <CardTitle className={`text-[#232833] dark:text-[#ffffff] ${isMobile ? 'text-sm' : ''}`}>Total Gameplays</CardTitle>
            </CardHeader>
            <CardContent className={`text-[#232833] dark:text-[#ffffff] ${isMobile ? 'pt-0' : ''}`}>
              {generalStatistics?.["Total Gameplays"] ? <SimpleGraph x={generalStatistics["date"]} y={generalStatistics["Total Gameplays"]} type="scatter" isMobile={isMobile} /> : <p>Loading total gameplays...</p>}
            </CardContent>
          </Card>
          <Card className="bg-[#ffffff] text-[#232833] border-0 shadow-none rounded-lg dark:bg-[#2c313c] dark:text-[#ffffff] overflow-hidden">
            <CardHeader className={isMobile ? 'pb-2' : ''}>
              <CardTitle className={`flex items-center text-[#232833] dark:text-[#ffffff] ${isMobile ? 'text-sm' : ''}`}>
                Total Users
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className={`ml-2 ${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Total Unique Users who entered the game.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
            </CardHeader>
            <CardContent className={`text-[#232833] dark:text-[#ffffff] ${isMobile ? 'pt-0' : ''}`}>
              {generalStatistics?.["Total Unique Users who entered the game"] ? (
                <SimpleGraph x={generalStatistics["date"]} y={generalStatistics["Total Unique Users who entered the game"]} type="scatter" isMobile={isMobile} />
              ) : (
                <p>Loading total users...</p>
              )}
            </CardContent>
          </Card>
          <Card className="bg-[#ffffff] text-[#232833] border-0 shadow-none rounded-lg dark:bg-[#2c313c] dark:text-[#ffffff] overflow-hidden">
            <CardHeader className={isMobile ? 'pb-2' : ''}>
              <CardTitle className={`text-[#232833] dark:text-[#ffffff] ${isMobile ? 'text-sm' : ''}`}>Total Advertisements</CardTitle>
            </CardHeader>
            <CardContent className={`text-[#232833] dark:text-[#ffffff] ${isMobile ? 'pt-0' : ''}`}>
              {generalStatistics?.["Total Ads"] ? <SimpleGraph x={generalStatistics["date"]} y={generalStatistics["Total Ads"]} type="scatter" isMobile={isMobile} /> : <p>Loading total advertisements...</p>}
            </CardContent>
          </Card>
          <Card className="bg-[#ffffff] text-[#232833] border-0 shadow-none rounded-lg dark:bg-[#2c313c] dark:text-[#ffffff] overflow-hidden">
            <CardHeader className={isMobile ? 'pb-2' : ''}>
              <CardTitle className={`text-[#232833] dark:text-[#ffffff] ${isMobile ? 'text-sm' : ''}`}>10 Day Retention</CardTitle>
            </CardHeader>
            <CardContent className={`text-[#232833] dark:text-[#ffffff] ${isMobile ? 'pt-0' : ''}`}>
              {retention?.day1_retention_percentage?.[0] !== undefined ? (
                <SimpleGraph
                  x={["Day 0", "Day 1", "Day 7", "Day 10"]}
                  y={[100, retention["day1_retention_percentage"][0], retention["day7_retention_percentage"][0], retention["day10_retention_percentage"][0]]}
                  type="bar"
                  isMobile={isMobile}
                />
              ) : (
                <p>Loading retention rates...</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Card>
  );
};

export default FourGraph;
